/**
 * RabattBlock — der Preisblock einer Second-Life-Karte (Teil 1, 2026-10-04).
 *
 * @description
 *   Nimmt den Rabatt in Prozent entgegen und zeigt sofort daneben, was daraus
 *   wird: B-Preis statt A-Preis und die Netto-Marge, gerechnet wie im Backend
 *   (`rabatt.js`). Darunter EK und der Rabattvorschlag des Modells mit
 *   Begruendung. Unter EK (netto) ist das Speichern gesperrt, und der Hinweis
 *   nennt den hoechsten zulaessigen Rabatt. Ersetzt das fruehere Preisfeld der
 *   Karte — nicht zusaetzlich.
 *
 *   🔴 **Zwei Zeilen, nicht fuenf** (Regel-16-Gate, 2026-10-05): die erste
 *   Fassung stapelte A-Preis, Vorschlag, „Suggest again", Feld mit Hilfetext
 *   und Bestaetigt-Zeile untereinander und machte die Karte gemessen um
 *   ~100 px (1280 px Breite) bzw. ~200 px (390 px) hoeher — gegen die Spec
 *   („in der bestehenden Karte, nicht grösser") und gegen den Owner-Wunsch nach
 *   kompakteren Karten. Das Feld hat deshalb eine feste, schmale Breite statt
 *   `fullWidth` (`pattern-library.md` §Form): es nimmt hoechstens „99,9" auf,
 *   und nur so passt das Ergebnis in dieselbe Zeile.
 *
 * @useWhen
 *   - Second-Life-Karte einer Aggregat-Zeile (je Artikel x Stufe)
 *
 * @dontUseFor
 *   - Einheiten-Zeilen — dort entsteht kein Produkt und kein Preis
 *   - allgemeine Preisbearbeitung — die gehoert in die Produktseiten
 *
 * @props
 *   zeile        Zeile aus /api/second-life/bestand (mit `vorschlag.a_vk/.ek`)
 *   busy         die Karte arbeitet gerade (Anlegen/Freigeben)
 *   onGeaendert  nach Save/Suggest again: (zeile, hinweisText) — die Seite laedt
 *                neu. 🔴 **`zeile` kam am 2026-10-16 dazu** (Luecke 2): der
 *                Fehlerslot der Seite nennt die Zeile, zu der eine Meldung
 *                gehoert. Der Block KENNT sie ohnehin (`zeile` ist sein Prop) —
 *                er hat sie bis dahin nur verworfen, und damit war
 *                `rabattGeaendert` der EINZIGE der acht Aufrufer, der seine
 *                Zeile nicht nennen konnte.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { rabattVorschlag, setRabatt } from '../../api/secondLife';
import { SP } from '../../theme/tokens';
import {
  alsCent,
  bPreisCent,
  eingabeZehntel,
  ekBekannt,
  feldVorbelegung,
  haeltEk,
  margeNettoCent,
  maxRabattZehntel,
} from './rabatt';

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const euroAusCent = (cent) => (cent == null ? '—' : EUR.format(cent / 100));
const prozent = (zehntel) =>
  `${(zehntel / 10).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

//: Klartext fuer die Fehlercodes der Rabatt-, Vorschlags-, Anlage- und
//: Freigabe-Routen. Ein Code, der hier fehlt, erscheint NICHT roh in der
//: Oberflaeche — der Rueckfall ist ein allgemeiner Satz (Regel-16-Gate W1).
export const RABATT_FEHLER = {
  kein_rabatt: 'Save a discount first — without it no price is created.',
  unter_ek: 'The discount goes below the cost price (net).',
  ek_fehlt: 'No cost price is stored for the base product — the lower bound cannot be checked.',
  a_vk_unter_ek: 'The A price is itself below the cost price — no discount is possible.',
  a_vk_fehlt: 'The A variant of this SKU has no price in Shopify.',
  kein_grundprodukt: 'No base product for this item number.',
  rabatt_ausserhalb: 'The discount must be between 0.1 and 99.9 %.',
  rabatt_genauigkeit: 'At most one decimal place.',
  preis_ungueltig: 'With this discount no price would remain.',
  shopify_fehler: 'Shopify did not accept the prices. Nothing was saved.',
  shopify_ohne_antwort:
    'Shopify did not confirm the write. Please check the draft in Shopify ' +
    'and try again.',
  zeile_fehlt: 'This row no longer exists — please reload the page.',
  keine_aggregat_zeile: 'Only one row per item and level gets a price.',
  keine_zahl: 'The model named no usable discount — please set it yourself.',
  keine_begruendung: 'The model named no reason — please set it yourself.',
};
const ALLGEMEIN_SPEICHERN = 'Saving failed. Please try again.';
const ALLGEMEIN_VORSCHLAG = 'No suggestion possible — please set the discount yourself.';

const QUELLE = {
  ek_begrenzt: 'limited by cost price',
  kein_pruefbefund: null,
};

function quelleText(quelle) {
  if (!quelle) return null;
  if (quelle in QUELLE) return QUELLE[quelle];
  return quelle.startsWith('llm:') ? 'Model' : null;
}

function vorschlagMeldung(r) {
  if (r.vorgeschlagen) return `New suggestion: ${prozent(Math.round(r.pct * 10))}.`;
  if (r.quelle === 'kein_pruefbefund') {
    return 'No stock check finding — please set the discount yourself.';
  }
  // `modell_fehler: <Klasse>` und alles Unbekannte bleiben unsichtbar.
  return RABATT_FEHLER[r.grund] || ALLGEMEIN_VORSCHLAG;
}

export default function RabattBlock({ zeile, busy, onGeaendert }) {
  const gespeichert = zeile.rabatt_pct ?? zeile.rabatt_vorschlag_pct;
  // 🔴 **Das Feld nimmt Zehntelprozent auf — `feldVorbelegung` ist der eine Weg
  // dorthin** (Regel-16-Befund B1, 2026-10-13, im Browser gemessen). Die Route
  // liefert `rabatt_pct` als ZEICHENKETTE mit zwei Dezimalen (`'15.00'`);
  // `eingabeZehntel` laesst hoechstens EINE Dezimale zu. Ohne Umrechnung oeffnete
  // das Feld ungueltig: rot, `aria-invalid`, „Save discount" gesperrt — auf JEDER
  // Karte, ohne dass jemand etwas getan hatte. Die Begruendung steht bei der
  // Funktion (`rabatt.js`), damit hier kein zweiter Weg entsteht.
  //
  // 🔴 **Und genau der zweite Weg war der Fehler.** Der Fix am Anfangszustand
  // allein wirkte nicht: der Effekt darunter setzte das Feld danach NOCH EINMAL
  // mit der alten Umwandlung, also stand wieder `'15,00'` darin. Zwei Stellen,
  // die dieselbe Umrechnung je selbst schreiben, sind eine Einheit zu viel —
  // deshalb ruft jetzt BEIDE Stellen `feldVorbelegung`. Wer hier wieder ein
  // `String(...).replace(...)` hinschreibt, holt den Fehler zurueck.
  const [eingabe, setEingabe] = useState(() => feldVorbelegung(gespeichert));
  // null | 'speichern' | 'vorschlag' — der Knopf, der gerade arbeitet, sagt es.
  const [arbeitet, setArbeitet] = useState(null);
  const [fehler, setFehler] = useState(null);
  const feldRef = useRef(null);
  const ergebnisId = useId();

  // Neu laden (anderer Vorschlag, gespeicherter Rabatt) setzt das Feld zurueck.
  // 🔴 Der Riegel „nur bei echter Aenderung" ist der DEP selbst: `gespeichert`
  // ist ein Primitivwert (Zahl oder Zeichenkette), kein Objekt. Ein neuer
  // `zeile`-Bezug mit gleichem Rabatt laesst den Dep unveraendert und den Effekt
  // aus. Wer den Dep auf `[zeile]` zieht, verliert den Riegel und ueberschreibt
  // die Eingabe des Nutzers bei jedem Neuladen.
  useEffect(() => {
    setEingabe(feldVorbelegung(gespeichert));
  }, [gespeichert]);

  const aVk = alsCent(zeile.vorschlag?.a_vk);
  const ek = alsCent(zeile.vorschlag?.ek);
  const zehntel = eingabeZehntel(eingabe);
  const ohneGrundlage = aVk == null || !ekBekannt(ek);
  const max = ohneGrundlage ? null : maxRabattZehntel(aVk, ek);
  const gesperrt = ohneGrundlage || max === null;
  const preis = !gesperrt && zehntel != null ? bPreisCent(aVk, zehntel) : null;
  const unterEk = preis != null && !haeltEk(preis, ek);
  const unveraendert =
    zeile.rabatt_pct != null && zehntel != null && Math.round(zeile.rabatt_pct * 10) === zehntel;

  // Die Ergebniszeile neben dem Feld. Rot NUR bei Fehlern der Eingabe — eine
  // fehlende Grundlage kann der Bediener hier nicht reparieren (W4); sie sperrt
  // das Feld und steht als Hinweis da.
  let ergebnis = '';
  let feldFehler = false;
  if (!zeile.vorschlag) {
    ergebnis = 'The price basis is missing — the suggestion was not loaded.';
  } else if (!ekBekannt(ek)) {
    ergebnis = RABATT_FEHLER.ek_fehlt;
  } else if (aVk == null) {
    ergebnis = RABATT_FEHLER.a_vk_fehlt;
  } else if (max === null) {
    ergebnis = RABATT_FEHLER.a_vk_unter_ek;
  } else if (eingabe.trim() === '') {
    ergebnis = `Enter a discount — at most ${prozent(max)}.`;
  } else if (zehntel == null) {
    ergebnis = 'Between 0.1 and 99.9 %, at most one decimal place.';
    feldFehler = true;
  } else if (unterEk) {
    ergebnis = `Below cost price (net) — at most ${prozent(max)} possible.`;
    feldFehler = true;
  } else {
    const rechnung =
      `B price ${euroAusCent(preis)} instead of ${euroAusCent(aVk)} · ` +
      `margin net ${euroAusCent(margeNettoCent(preis, ek))}`;
    if (unveraendert) {
      // „im Entwurf" nur, wenn der Entwurf diesen Rabatt wirklich traegt. Wurde
      // der Rabatt bei ausgeschalteter Anlage geaendert, steht dort noch der
      // alte Preis — das sagt die Zeile dann ausdruecklich (Regel-15-Gate W5).
      const entwurfAktuell =
        alsCent(zeile.price_eur) === preis && alsCent(zeile.vergleichspreis_eur) === aVk;
      ergebnis = entwurfAktuell
        ? `Saved — in the draft ${EUR.format(zeile.price_eur)} instead of ${EUR.format(zeile.vergleichspreis_eur)}`
        : zeile.price_eur != null
          ? `Saved — ${rechnung}; the draft still shows ${EUR.format(zeile.price_eur)}`
          : `Saved — ${rechnung}`;
    } else {
      // Vorschlag oder Aenderung: das Feld allein sieht aus wie ein
      // gespeicherter Wert, deshalb sagt die Zeile es dazu (W6).
      ergebnis = `${rechnung} — not yet saved`;
    }
  }
  const kannSpeichern =
    !busy && !arbeitet && !gesperrt && zehntel != null && !unterEk && !unveraendert;

  const speichern = async () => {
    setFehler(null);
    setArbeitet('speichern');
    try {
      const r = await setRabatt(zeile.id, zehntel / 10);
      onGeaendert(
        zeile,
        r.in_shopify
          ? 'Discount saved — price, new price and cost price updated in the Shopify draft.'
          : r.hinweis === 'anlage_aus'
            ? 'Discount noted — not written to Shopify, because creation is switched off.'
            : 'Discount saved — the prices are created on creation.'
      );
      // Der Knopf ist danach gesperrt (unveraendert) und verloere den Fokus an
      // `body`; er geht zurueck ans Feld (W5).
      feldRef.current?.focus();
    } catch (e) {
      const d = e?.response?.data || {};
      setFehler(RABATT_FEHLER[d.error] || ALLGEMEIN_SPEICHERN);
    } finally {
      setArbeitet(null);
    }
  };

  const neuVorschlagen = async () => {
    setFehler(null);
    setArbeitet('vorschlag');
    try {
      onGeaendert(zeile, vorschlagMeldung(await rabattVorschlag(zeile.id)));
    } catch (e) {
      setFehler(RABATT_FEHLER[e?.response?.data?.error] || ALLGEMEIN_VORSCHLAG);
    } finally {
      setArbeitet(null);
    }
  };

  const quelle = quelleText(zeile.rabatt_quelle);
  const vorschlagText =
    zeile.rabatt_vorschlag_pct != null
      ? `Suggestion ${prozent(Math.round(zeile.rabatt_vorschlag_pct * 10))}` +
        (quelle ? ` (${quelle})` : '') +
        (zeile.rabatt_begruendung ? `: ${zeile.rabatt_begruendung}` : '')
      : zeile.rabatt_quelle === 'kein_pruefbefund'
        ? 'No stock check finding — please set the discount yourself.'
        : 'No suggestion yet.';
  const grundlage = `Cost ${euroAusCent(ek)} net · ${vorschlagText}`;

  return (
    <Stack spacing={SP.chip}>
      <Stack direction="row" spacing={SP.tight} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          label="Discount in %"
          size="small"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          error={feldFehler}
          disabled={gesperrt}
          inputRef={feldRef}
          inputProps={{ inputMode: 'decimal', 'aria-describedby': ergebnisId }}
          // 🔴 `spacing(12)` statt `spacing(16)` (2026-10-12): die Karte ist
          // schmaler geworden (drei Karten im Bild statt zwei), und mit 128 px
          // Feldbreite passte „Save discount" daneben nicht mehr in EINE Zeile —
          // die Zeile waere umgebrochen und die Karte um ~40 px HOEHER, genau
          // gegen den Zweck des Umbaus. Das Feld nimmt „99,9" auf; 96 px reichen.
          sx={{ width: (t) => t.spacing(12) }}
        />
        {/* 🔴 **Der gesperrte Knopf nennt seinen Grund** (Plan §3.1, 2026-10-14).
            Der Satz `ergebnisId` steht unmittelbar daneben — er traegt die
            Begruendung, den Eingabefehler und den Speicherzustand — und ist per
            `tabIndex={0}` fokussierbar; es fehlte nur die VERKETTUNG. KEIN
            zweiter, `visuallyHidden`-Satz daneben: der laege doppelt im DOM und
            ein Text-Locator faende zwei Elemente. ⚠️ Der Knopf bleibt `disabled`
            (die Plattform traegt die Sperre) — der Weg im FOKUSMODUS ist der
            fokussierbare Satz daneben, nicht der Knopf (Plan §3.3). */}
        <Button
          variant="outlined"
          size="small"
          onClick={speichern}
          disabled={!kannSpeichern}
          aria-describedby={ergebnisId}
        >
          {arbeitet === 'speichern' ? 'Saving…' : 'Save discount'}
        </Button>
        {/* 🔴 **Hoechstens ZWEI Zeilen, der Rest im Tooltip** (2026-10-12). Die
            Zeile nennt B-Preis, Marge und den Speicherzustand — auf der schmalen
            Karte sind das drei bis vier Zeilen, und die Karte waechst mit jeder.
            Zwei Zeilen sind die Obergrenze; alles darueber steht vollstaendig im
            Tooltip (dieselbe Form wie `grundlage` darunter) und bleibt fuer
            Screenreader der ganze Text — N11.3 „nichts geht verloren" gilt fuer
            das Feld, nicht fuer seine Zeilenhoehe.
            🔴 `tabIndex={0}`: ohne ihn kaeme der Tooltip nur mit der Maus an. */}
        <Tooltip title={ergebnis} placement="bottom-start">
          <Typography
            id={ergebnisId}
            variant="body2"
            color={feldFehler ? 'error' : 'text.primary'}
            tabIndex={0}
            // Mindestbreite: auf schmalen Schirmen rutscht die Zeile als Ganzes unter
            // Feld und Knopf, statt neben ihnen in ~60 px zu zwoelf Zeilen zu brechen
            // (gemessen 2026-10-05 bei 390 px).
            sx={{
              flex: '1 1 auto',
              flexBasis: (t) => t.spacing(30),
              minWidth: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {ergebnis}
          </Typography>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={SP.tight} alignItems="center" sx={{ minWidth: 0 }}>
        {/* Die Begruendung kann lang sein: eine Zeile, abgeschnitten, ganz im
            Tooltip — der Text ist fokussierbar, damit der Tooltip auch per
            Tastatur erreichbar ist. */}
        <Tooltip title={grundlage} placement="bottom-start">
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            tabIndex={0}
            sx={{ minWidth: 0, flex: 1 }}
          >
            {grundlage}
          </Typography>
        </Tooltip>
        <Button
          size="small"
          variant="text"
          onClick={neuVorschlagen}
          disabled={busy || !!arbeitet}
          sx={{ flexShrink: 0, py: 0 }}
        >
          {arbeitet === 'vorschlag' ? 'Suggesting…' : 'Suggest again'}
        </Button>
      </Stack>
      {fehler && (
        <Typography variant="caption" color="error" role="alert">
          {fehler}
        </Typography>
      )}
    </Stack>
  );
}
